'use client'

import clsx from 'clsx'
import { useTranslations } from '@/lib/next-shim'
import { BookApppointmentButton } from './BookAppointmentButton'
import { ConsultNowButton } from './ConsultNowButton'
import { Hero } from './Hero'
import { Specialities } from './Specialities'
import { Staffs } from './Staffs'
import { Summary } from './Summary'
import { Description } from './Description'
import { useGetMeetingLinkQuery } from '@/core/graphql/hooks'
import { useRequiredParams } from '@/hooks/useRequiredParams'

export function MeetingLinkPage() {
  const t = useTranslations()

  const { meetingLink: meetingLinkSlug } = useRequiredParams(['meetingLink'])

  const { data, loading, error } = useGetMeetingLinkQuery({
    variables: { slug: meetingLinkSlug },
    skip: !meetingLinkSlug,
  })

  if (loading) return <div className="p-10 text-center">{t('common.loading')}</div>
  if (error) return <div className="p-10 text-center text-red-500">{t('common.error', { message: error.message })}</div>

  const meetingLink = data?.meetingLink
  if (!meetingLink) return <div className="p-10 text-center">{t('common.meetingLinkNotFound')}</div>

  const isImmediateEnabled = meetingLink.immediateConsultationConfig?.enabled && !!meetingLink.waitingRoom

  return (
    <main className={clsx('flex-1 bg-gray-50 sm:pb-0', isImmediateEnabled ? 'pb-44' : 'pb-24')}>
      <Hero meetingLink={meetingLink} meetingLinkSlug={meetingLinkSlug} />

      {/* Main content */}
      <div className="relative -mt-12 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Summary — first on mobile so floating CTA doesn't cover it; right column on desktop */}
          <div className="space-y-6 lg:order-last lg:col-span-1">
            <Summary meetingLink={meetingLink} meetingLinkSlug={meetingLinkSlug} />
          </div>

          {/* Description / Specialities / Staffs — after Summary on mobile, first 2 cols on desktop */}
          <div className="lg:col-span-2 space-y-6">
            <Description description={meetingLink.description ?? ''} />
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <Specialities meetingLink={meetingLink} title={t('page.MeetingLink.specialitiesSection.title')} />
            </div>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <Staffs meetingLink={meetingLink} title={t('page.MeetingLink.staffsSection.title')} />
            </div>
          </div>
        </div>
      </div>

      {/* Floating CTA button for mobile */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white shadow-md border-t border-gray-200 z-50 flex flex-col gap-3">
        {isImmediateEnabled && (
          <ConsultNowButton
            className="w-full px-4 font-semibold text-primary border-2 border-primary hover:bg-primary-50 transition-all flex items-center justify-center gap-2"
            meetingLinkSlug={meetingLinkSlug}
          />
        )}
        <BookApppointmentButton className="w-full px-4 font-semibold text-white bg-primary hover:bg-primary-dark" meetingLinkSlug={meetingLinkSlug} />
      </div>
    </main>
  )
}
